/**
 * Pure view logic for the onboarding stage funnel.
 *
 * Separate from the .tsx for the same reason onboardingProgressLogic.ts is on
 * the native side: the rules that decide what a reader BELIEVES deserve direct
 * unit tests, without dragging visx/lucide/Tailwind through jest.
 *
 * THE RULE THIS MODULE EXISTS FOR. The backend computes
 * `partially_instrumented` for each step: true when the selected range reaches
 * back past the date that step's signal started being collected. A step in that
 * state has a LOW COUNT FOR A REASON THAT IS NOT DROP-OFF — those users predate
 * the instrumentation. Drawing it as an ordinary funnel bar with a drop-off
 * label is a lie, and a confident-looking one.
 *
 * Concretely: on the default 30-day range, `meeting_your_coach` (shipped
 * 2026-08-14) covers only part of the cohort. Rendered naively it looks like a
 * catastrophic drop at step 2. It is not.
 */

import type { OnboardingFunnelStep } from './types';

export type StepState = 'measured' | 'partial' | 'unknown';

export interface OnboardingBarRow {
  key: string;
  label: string;
  count: number;
  /** Width relative to the cohort (step 0), 0-100. Never NaN. */
  widthPct: number;
  state: StepState;
  availableFrom: string | null;
  pctOfCohort: number | null;
  pctOfPrior: number | null;
  dropoffFromPrior: number | null;
  /**
   * Whether a drop-off figure may be DRAWN for this step. False whenever the
   * step is not fully measured — the number would be arithmetically correct and
   * substantively false.
   */
  showDropoff: boolean;
}

export function stepState(step: OnboardingFunnelStep): StepState {
  if (step.partially_instrumented === null) return 'unknown';
  return step.partially_instrumented ? 'partial' : 'measured';
}

export function barRows(steps: OnboardingFunnelStep[]): OnboardingBarRow[] {
  if (!steps.length) return [];
  const cohort = steps[0].count;
  return steps.map((step, i) => {
    const state = stepState(step);
    return {
      key: step.key,
      label: step.label,
      count: step.count,
      // Guard the zero cohort explicitly: 0/0 is NaN, and a NaN width renders
      // as an invisible bar rather than an obviously-broken one.
      widthPct: cohort > 0 ? Math.min(100, (step.count / cohort) * 100) : 0,
      state,
      availableFrom: step.available_from,
      pctOfCohort: step.pct_of_cohort,
      pctOfPrior: step.pct_of_prior,
      dropoffFromPrior: step.dropoff_from_prior,
      showDropoff: i > 0 && state === 'measured' && step.dropoff_from_prior !== null,
    };
  });
}

/**
 * One sentence naming every step whose numbers are not trustworthy for the
 * selected range, or null when they all are.
 *
 * Names them explicitly rather than showing a generic caveat: a reader who
 * cannot tell WHICH bar to distrust will either distrust all of them or, more
 * likely, none.
 */
export function instrumentationNotice(steps: OnboardingFunnelStep[]): string | null {
  const affected = steps.filter((s) => stepState(s) !== 'measured');
  if (!affected.length) return null;
  const parts = affected.map((s) =>
    s.available_from ? `${s.label} (collected from ${s.available_from})` : `${s.label} (collection date unknown)`,
  );
  return (
    `This range reaches back before some signals existed, so these steps undercount ` +
    `rather than showing drop-off: ${parts.join('; ')}. Narrow the range for a like-for-like comparison.`
  );
}
